import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, Plus, Edit3, Save, X, Power, Filter } from 'lucide-react';
import { LOJAS_MANUTENCAO } from '../../../utils/empresas';

// =====================================================================
// Cadastro de equipamentos FÍSICOS (ativos) — CRUD. Cada equipamento real é
// único pelo PATRIMÔNIO (Nº do Ativo). Os relatórios linkam por ID.
// =====================================================================

const API = '/api/manutencao/ativos';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const inputStyle = { width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6, boxSizing: 'border-box' };
const labelStyle = { fontSize: 12, fontWeight: 700, color: '#64748b' };
const btnVoltar = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 700, cursor: 'pointer' };

// codigo = null → cadastro novo (o código é gerado pelo servidor); preenchido → edição.
const VAZIO = { codigo: null, numero_ativo: '', id_equipamento: '', loja: '', fabricante: '', modelo: '', numero_serie: '', ano_fabricacao: '', nome_conta: '', denominacao: '', data_incorporacao: '', ativo: 1 };

const AtivosCadastro = ({ onVoltar }) => {
  const [lista, setLista] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [form, setForm] = useState(null); // null = lista; objeto = editando/criando

  // Filtros da lista (client-side — a lista é pequena e o filtro é instantâneo).
  const [fLoja, setFLoja] = useState('');
  const [fTipo, setFTipo] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const [rl, rt] = await Promise.all([
        fetch(API, { headers: auth() }),
        fetch(`${API}/tipos`, { headers: auth() }),
      ]);
      if (!rl.ok) throw new Error(`HTTP ${rl.status}`);
      setLista(await rl.json());
      setTipos(rt.ok ? await rt.json() : []);
    } catch (e) {
      setErro('Erro ao carregar equipamentos.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    setErro('');
    if (!form.numero_ativo?.trim()) { setErro('Informe o patrimônio (Nº do Ativo).'); return; }
    if (!form.id_equipamento) { setErro('Escolha o tipo de equipamento.'); return; }
    const metodo = form.codigo ? 'PUT' : 'POST';
    const url = form.codigo ? `${API}/${form.codigo}` : API;
    const r = await fetch(url, {
      method: metodo,
      headers: { ...auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro ao salvar.'); return; }
    setForm(null);
    carregar();
  };

  // Opções dos filtros derivadas dos próprios dados (só o que existe na lista).
  const lojas = useMemo(() => [...new Set(lista.map((a) => a.LOJA).filter(Boolean))].sort(), [lista]);
  const tiposLista = useMemo(() => [...new Set(lista.map((a) => a.EQUIPAMENTO).filter(Boolean))].sort(), [lista]);
  const listaFiltrada = useMemo(() => lista.filter((a) => {
    if (fLoja && a.LOJA !== fLoja) return false;
    if (fTipo && a.EQUIPAMENTO !== fTipo) return false;
    return true;
  }), [lista, fLoja, fTipo]);
  const temFiltro = fLoja || fTipo;

  // ---- Formulário (criar/editar) ----
  if (form) {
    const set = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <button onClick={() => setForm(null)} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
          <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{form.codigo ? 'Editar equipamento' : 'Novo equipamento'}</h3>
        </div>
        {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>{erro}</div>}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            {form.codigo && (
              <div>
                <label style={labelStyle}>Código do equipamento</label>
                {/* Código é a identidade (PK) — gerado na criação e não editável. */}
                <input style={{ ...inputStyle, background: '#f1f5f9', fontWeight: 800, letterSpacing: 0.5 }} value={form.codigo} readOnly />
              </div>
            )}
            <div>
              <label style={labelStyle}>Patrimônio (Nº do Ativo) *</label>
              <input style={inputStyle} value={form.numero_ativo} onChange={(e) => set('numero_ativo', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Tipo de equipamento *</label>
              <select style={inputStyle} value={form.id_equipamento} onChange={(e) => set('id_equipamento', e.target.value)}>
                <option value="">— escolher —</option>
                {tipos.map((t) => <option key={t.ID} value={t.ID}>{t.NOME}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Loja</label>
              <select style={inputStyle} value={form.loja} onChange={(e) => set('loja', e.target.value)}>
                <option value="">— escolher —</option>
                {LOJAS_MANUTENCAO.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            {/* Campos SAP (padrão "QUADRO DE ATIVOS"). Denominação distingue Batatas/Carnes. */}
            <div><label style={labelStyle}>Denominação (SAP)</label><input style={inputStyle} value={form.denominacao} onChange={(e) => set('denominacao', e.target.value)} placeholder="Ex.: FRITADEIRA DE BATATAS" /></div>
            <div><label style={labelStyle}>Nome da conta (SAP)</label><input style={inputStyle} value={form.nome_conta} onChange={(e) => set('nome_conta', e.target.value)} placeholder="Ex.: EQUIPAMENTOS E UTENSILIOS" /></div>
            <div><label style={labelStyle}>Data de incorporação (SAP)</label><input type="date" style={inputStyle} value={form.data_incorporacao} onChange={(e) => set('data_incorporacao', e.target.value)} /></div>
            <div><label style={labelStyle}>Fabricante</label><input style={inputStyle} value={form.fabricante} onChange={(e) => set('fabricante', e.target.value)} /></div>
            <div><label style={labelStyle}>Modelo</label><input style={inputStyle} value={form.modelo} onChange={(e) => set('modelo', e.target.value)} /></div>
            <div><label style={labelStyle}>Nº de Série</label><input style={inputStyle} value={form.numero_serie} onChange={(e) => set('numero_serie', e.target.value)} /></div>
            <div><label style={labelStyle}>Ano de Fabricação</label><input type="number" style={inputStyle} value={form.ano_fabricacao} onChange={(e) => set('ano_fabricacao', e.target.value)} /></div>
            {form.codigo != null && (
              <div>
                <label style={labelStyle}>Situação</label>
                <select style={inputStyle} value={form.ativo} onChange={(e) => set('ativo', Number(e.target.value))}>
                  <option value={1}>Ativo</option>
                  <option value={0}>Inativo</option>
                </select>
              </div>
            )}
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <button onClick={salvar} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 20px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, cursor: 'pointer' }}>
              <Save size={16} /> Salvar
            </button>
            <button onClick={() => setForm(null)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
              <X size={16} /> Cancelar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Lista ----
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
        <h3 style={{ margin: 0, flex: 1, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Equipamentos cadastrados</h3>
        <button onClick={() => setForm({ ...VAZIO })} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
          <Plus size={16} /> Novo equipamento
        </button>
      </div>

      {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>{erro}</div>}

      {/* Barra de filtros (loja + tipo de equipamento) — só com dados na lista */}
      {lista.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, marginBottom: 16 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#475569', fontWeight: 700, fontSize: 13 }}><Filter size={15} /> Filtros</span>
          <select value={fLoja} onChange={(e) => setFLoja(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, color: '#475569', background: '#fff' }}>
            <option value="">Todas as lojas</option>
            {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <select value={fTipo} onChange={(e) => setFTipo(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, color: '#475569', background: '#fff' }}>
            <option value="">Todos os tipos</option>
            {tiposLista.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          {temFiltro && (
            <button onClick={() => { setFLoja(''); setFTipo(''); }} style={{ padding: '8px 12px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Limpar</button>
          )}
          <span style={{ marginLeft: 'auto', fontSize: 13, color: '#94a3b8' }}>{listaFiltrada.length} de {lista.length}</span>
        </div>
      )}

      {carregando ? <p style={{ color: '#64748b' }}>Carregando…</p> : lista.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>Nenhum equipamento cadastrado.</div>
      ) : listaFiltrada.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>Nenhum equipamento para os filtros escolhidos.</div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {listaFiltrada.map((a) => (
            <div key={a.CODIGO} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, opacity: a.ATIVO ? 1 : 0.6 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 12, fontWeight: 800, background: '#eef2ff', color: '#4338ca', letterSpacing: 0.5, fontFamily: 'monospace' }}>{a.CODIGO}</span>
                  <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 800, background: '#e0e7ff', color: '#4338ca' }}>{a.EQUIPAMENTO}</span>
                  <span style={{ fontWeight: 800, color: '#0f172a' }}>Patrimônio {a.NUMERO_ATIVO}</span>
                  {a.DENOMINACAO && <span style={{ fontSize: 13, color: '#334155' }}>· {a.DENOMINACAO}</span>}
                  {!a.ATIVO && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#94a3b8' }}><Power size={12} /> inativo</span>}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                  {a.LOJA || 'sem loja'}{a.FABRICANTE ? ` · ${a.FABRICANTE}` : ''}{a.MODELO ? ` ${a.MODELO}` : ''}{a.NUMERO_SERIE ? ` · série ${a.NUMERO_SERIE}` : ''}{a.ANO_FABRICACAO ? ` · ${a.ANO_FABRICACAO}` : ''}
                </div>
              </div>
              <button onClick={() => setForm({
                codigo: a.CODIGO, numero_ativo: a.NUMERO_ATIVO, id_equipamento: a.ID_EQUIPAMENTO, loja: a.LOJA || '',
                fabricante: a.FABRICANTE || '', modelo: a.MODELO || '', numero_serie: a.NUMERO_SERIE || '',
                ano_fabricacao: a.ANO_FABRICACAO || '',
                nome_conta: a.NOME_CONTA || '', denominacao: a.DENOMINACAO || '',
                // DATA_INCORPORACAO vem como ISO com hora; input date precisa de YYYY-MM-DD.
                data_incorporacao: a.DATA_INCORPORACAO ? String(a.DATA_INCORPORACAO).slice(0, 10) : '',
                ativo: a.ATIVO,
              })} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: '#eef2ff', color: '#4338ca', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                <Edit3 size={14} /> Editar
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AtivosCadastro;
